import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DataGrid } from '@mui/x-data-grid';
import {
	Stack,
	Typography,
	Switch,
	Tooltip,
	Button,
	Box,
	CircularProgress,
	IconButton,
	useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { showToast } from '@/features/toast/toastSlice';
import { openDialog } from '@/features/dialog/dialogSlice';
import {
	useListBrokerComplianceStepsQuery,
	useUpdateBrokerComplianceStepMutation,
	useDeleteBrokerComplianceStepMutation,
	useInitializeBrokerComplianceStepsMutation,
} from '@/features/complianceSteps/complianceStepsApi';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { usePipelineContext } from '@/context/pipelineContext';
import {
	ColumnControlContextProvider,
	useDataGridColumnProps,
} from '@/context/columnControlContext';
import { formatDate, getStageLabels } from '@/utils/constants';
import { OwnerCellData } from '../OwnerCell';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { BrokerComplianceStepsFilters } from './brokerComplianceStepsFilters';

export const BrokerComplianceStepsTable = ({ pipelineId }) => (
	<ColumnControlContextProvider variant='brokerComplianceSteps' value={pipelineId}>
		<BrokerComplianceStepsTableContent pipelineId={pipelineId} />
	</ColumnControlContextProvider>
);

const BrokerComplianceStepsTableContent = ({ pipelineId }) => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const [stepToDelete, setStepToDelete] = useState(null);
	const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'active', 'inactive'
	const [stageFilter, setStageFilter] = useState('all');
	const account = useSelector((state) => state.msalAccount.account);
	const { data: userDetails } = useGetUserDetailsQuery({ email: account?.username });
	const canEdit = userDetails?.principleUser;

	// Get broker group array from user details
	const brokerGroupArray = userDetails?.brokerGroupArray || [];

	const [updateBrokerComplianceStep] = useUpdateBrokerComplianceStepMutation();
	const [deleteBrokerComplianceStep] = useDeleteBrokerComplianceStepMutation();
	const [initializeBrokerComplianceSteps, { isLoading: isInitLoading }] =
		useInitializeBrokerComplianceStepsMutation();

	const {
		data: questions,
		isLoading,
		isUninitialized,
		refetch,
	} = useListBrokerComplianceStepsQuery(
		{ brokerGroupArray, pipelineId, includeInactive: true },
		{ skip: !brokerGroupArray.length || !pipelineId },
	);

	const rows = questions ?? [];

	useEffect(() => {
		const checkAndInitializeSteps = async () => {
			if (
				!brokerGroupArray.length ||
				!pipelineId ||
				isLoading ||
				isInitLoading ||
				isUninitialized
			)
				return;
			if (rows.length === 0) {
				try {
					const result = await initializeBrokerComplianceSteps({
						brokerGroupArray,
					}).unwrap();
					console.log('created steps result: ', result);
				} catch (error) {
					console.error('Error initializing compliance steps:', error);
					dispatch(
						showToast({
							message: 'Error loading compliance steps',
							error: true,
						}),
					);
				}
			}
		};

		checkAndInitializeSteps();
	}, [brokerGroupArray, pipelineId, isLoading, isUninitialized]);

	const { stages } = usePipelineContext(pipelineId);
	const stageOrderMap = useMemo(() => {
		if (!stages) return new Map();
		return new Map(stages.map((stage) => [stage.id, stage.displayOrder ?? stage.order ?? 0]));
	}, [stages]);

	// Apply filters to rows
	const filteredRows = useMemo(() => {
		if (!rows.length) return rows;

		return rows.filter((row) => {
			// Active filter
			if (activeFilter === 'active' && !row.isActive) return false;
			if (activeFilter === 'inactive' && row.isActive) return false;

			// Stage filter
			if (stageFilter !== 'all' && row.stage !== stageFilter) return false;

			return true;
		});
	}, [rows, activeFilter, stageFilter]);

	const sortedRows = useMemo(() => {
		if (!filteredRows.length || !stages) return filteredRows;

		return [...filteredRows].sort((a, b) => {
			const stageIdA = a.stage;
			const stageIdB = b.stage;

			const orderA = stageOrderMap.get(stageIdA) ?? null;
			const orderB = stageOrderMap.get(stageIdB) ?? null;

			if (orderA === null && orderB === null) {
				return (a.order ?? 0) - (b.order ?? 0);
			}
			if (orderA === null) return 1;
			if (orderB === null) return -1;

			if (orderA !== orderB) {
				return orderA - orderB;
			}
			return (a.order ?? 0) - (b.order ?? 0);
		});
	}, [filteredRows, stages, stageOrderMap]);

	const handleUpdateStep = async (stepId, updates) => {
		try {
			await updateBrokerComplianceStep({ stepId, updates }).unwrap();
			dispatch(showToast({ message: 'Step updated successfully', success: true }));
		} catch {
			dispatch(showToast({ message: 'Error updating step', error: true }));
		}
	};

	const handleToggleActive = (row, isActive) => {
		handleUpdateStep(row.id, { isActive });
	};

	const handleDeleteStep = async (row) => {
		if (row?.questionRef) {
			return;
		}
		setStepToDelete(row);
	};

	const confirmDelete = async () => {
		if (!stepToDelete) return;

		try {
			await deleteBrokerComplianceStep({
				stepId: stepToDelete.id,
			}).unwrap();
			dispatch(showToast({ message: 'Step deleted successfully', success: true }));
		} catch (error) {
			console.log('Error deleting compliance step:', error);
			dispatch(showToast({ message: 'Error deleting step', error: true }));
		} finally {
			setStepToDelete(null);
		}
	};

	const handleAddStep = () => {
		dispatch(
			openDialog({
				dialogName: 'createComplianceStep',
				data: { brokerGroupArray, pipelineId },
			}),
		);
	};

	const processRowUpdate = async (newRow, oldRow) => {
		// If it has a questionRef, it's a default step and can't be edited
		if (oldRow?.questionRef) {
			return oldRow;
		}

		const changes = {};
		['name', 'description', 'order'].forEach((key) => {
			if (newRow[key] !== oldRow[key]) {
				changes[key] = key === 'order' ? Number(newRow[key]) : newRow[key];
			}
		});

		if (Object.keys(changes).length > 0) {
			await handleUpdateStep(newRow.id, changes);
		}
		return newRow;
	};

	const isDefaultStep = (row) => {
		return !!row?.questionRef;
	};

	const renderDefaultStepCell = (row, value) => {
		if (!isDefaultStep(row)) {
			return value;
		}

		return (
			<Tooltip title='Default step — only the active status can be changed'>
				<span
					style={{
						display: 'block',
						width: '100%',
						color: 'text.secondary',
					}}
				>
					{value ?? '—'}
				</span>
			</Tooltip>
		);
	};

	const columns = [
		{
			field: 'name',
			headerName: 'Name',
			flex: 1,
			editable: canEdit,
			renderCell: ({ row, value }) => renderDefaultStepCell(row, value),
			sortable: false,
			filterable: false,
		},
		{
			field: 'description',
			headerName: 'Description',
			flex: 2,
			sortable: false,
			filterable: false,
			editable: canEdit,
			renderCell: ({ row, value }) => renderDefaultStepCell(row, value),
		},
		{
			field: 'stages',
			headerName: 'Stage',
			flex: 1,
			sortable: false,
			filterable: false,
			renderCell: ({ row }) => {
				const stageId = row.stage;
				const stage = stages.find((s) => s.id === stageId);
				const displayText = stage?.label || stageId || '—';

				if (isDefaultStep(row)) {
					return (
						<Tooltip title='Default step — only the active status can be changed'>
							<span style={{ color: 'text.secondary' }}>{displayText}</span>
						</Tooltip>
					);
				}

				return displayText;
			},
		},
		{
			field: 'createdBy',
			headerName: 'Created By',
			width: 200,
			sortable: false,
			filterable: false,
			renderCell: ({ row }) => {
				const createdBy = row.createdBy;

				return createdBy ? (
					<Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
						{/* createdBy is the creator's Firestore user doc id. */}
						<OwnerCellData userId={createdBy} color='text.primary' />
					</Box>
				) : (
					<Typography sx={{ mt: 1 }}>{'Folio'}</Typography>
				);
			},
		},
		{
			field: 'lastUpdated',
			headerName: 'Last Updated',
			width: 200,
			sortable: false,
			filterable: false,
			renderCell: ({ row }) => (
				<Tooltip
					title={
						isDefaultStep(row)
							? 'Default step — only the active status can be changed'
							: ''
					}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
						<Typography>
							{formatDate(row?.lastUpdated?._seconds * 1000, true) || '—'}
						</Typography>
					</Box>
				</Tooltip>
			),
		},
		{
			field: 'isActive',
			headerName: 'Active',
			width: 120,
			sortable: false,
			filterable: false,
			renderCell: ({ row }) => {
				return (
					<Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
						<Switch
							disabled={!canEdit}
							checked={!!row.isActive}
							onChange={(e) => handleToggleActive(row, e.target.checked)}
						/>
					</Box>
				);
			},
		},
		{
			field: 'actions',
			headerName: 'Actions',
			width: 100,
			sortable: false,
			filterable: false,
			renderCell: ({ row }) => {
				const isDefault = isDefaultStep(row);
				return (
					<Tooltip
						title={
							isDefault
								? "Default step — you can't delete default steps"
								: 'Delete step'
						}
					>
						<Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
							<IconButton
								disabled={isDefault}
								onClick={() => handleDeleteStep(row)}
								sx={{ color: '#505AFC' }}
							>
								<DeleteIcon />
							</IconButton>
						</Box>
					</Tooltip>
				);
			},
		},
	];

	const { columns: columnsWithWidths, onColumnWidthChange } = useDataGridColumnProps(columns, {
		disableColumnVisibility: true,
	});

	const isLoadingState = isLoading && rows.length === 0;

	return (
		<>
			<Stack direction='column' spacing={2} sx={{ width: '100%', mt: 4 }}>
				{/* Added more space above Compliance Steps with mt: 4 and extra spacing */}
				<Stack direction='row' alignItems='center' justifyContent='space-between'>
					<Typography variant='h6' sx={{ fontWeight: 600 }}>
						Compliance Steps
					</Typography>
					{canEdit && !isLoadingState && (
						<Button
							variant='contained'
							disabled={!canEdit}
							startIcon={<AddIcon />}
							onClick={handleAddStep}
							sx={{
								minWidth: '140px', // Added minimum width for the button
								px: 3, // Added extra horizontal padding
							}}
						>
							Add Step
						</Button>
					)}
				</Stack>

				<BrokerComplianceStepsFilters
					stages={stages}
					activeFilter={activeFilter}
					stageFilter={stageFilter}
					onActiveFilterChange={setActiveFilter}
					onStageFilterChange={setStageFilter}
				/>

				<Box
					sx={{
						border: `1px solid ${theme.palette.divider}`,
						borderRadius: '8px',
						backgroundColor: theme.palette.background.paper,
						'& .MuiDataGrid-root': {
							borderRadius: '8px',
						},
						height: 600,
					}}
				>
					{isLoadingState ? (
						<CircularProgress />
					) : (
						<DataGrid
							showCellVerticalBorder
							disableRowSelectionOnClick
							disableSelectionOnClick
							disableColumnFilter
							disableColumnSelector
							disableColumnMenu
							hideFooterPagination
							hideFooter
							rows={sortedRows}
							columns={columnsWithWidths}
							onColumnWidthChange={onColumnWidthChange}
							getRowClassName={(params) =>
								isDefaultStep(params.row) ? 'default-step-row' : 'editable-step-row'
							}
							sx={{
								border: 'none',
								'& .MuiDataGrid-row:last-of-type .MuiDataGrid-cell': {
									borderBottom: 'none',
								},
								'& .default-step-row': {
									backgroundColor: (theme) => theme.palette.action.hover,
								},
								'& .default-step-row:hover': {
									backgroundColor: (theme) => theme.palette.action.hover,
								},
								'& .default-step-row .MuiDataGrid-cell': {
									color: (theme) => theme.palette.text.secondary,
								},
								'& .editable-step-row:hover': {
									backgroundColor: (theme) => theme.palette.background.paper,
								},
							}}
							editMode='cell'
							processRowUpdate={processRowUpdate}
							rowsPerPageOptions={[25, 50, 100]}
							isCellEditable={(params) => {
								if (!canEdit) return false;
								if (isDefaultStep(params.row)) return false;
								return true;
							}}
						/>
					)}
				</Box>
			</Stack>
			{stepToDelete && (
				<ConfirmDialog
					openDialog={!!stepToDelete}
					handleClose={() => setStepToDelete(null)}
					handleConfirm={confirmDelete}
					actionName={'delete compliance step'}
					additionalContent={
						'This will remove the compliance step from all deals and hide it from the table.'
					}
					canBeUndone={false}
				/>
			)}
		</>
	);
};
